/**
 * Consent-aware PostHog wrapper for Studio business events.
 *
 * Analytics is deliberately best-effort: it never blocks authentication,
 * navigation, checkout, or any other product flow.
 */
import type { AnalyticsConsent } from './analytics-consent'
import type { AuthOperationInput, AuthOperationRecorder } from './auth-telemetry'
import type { OAuthAnalyticsMethod } from './posthog-oauth-intent'
import type { AnalyticsUser } from './posthog-user'

import posthogDefault, { posthog as posthogNamed } from 'posthog-js'

import { GA4_MEASUREMENT_ID, POSTHOG_API_KEY, POSTHOG_HOST, POSTHOG_PROJECT_NAME, POSTHOG_TRACK_EVENTS, POSTHOG_UI_HOST } from '../constants'
import { clearPostHogPersistence, readAnalyticsConsent, writeAnalyticsConsent } from './analytics-consent'
import { sanitizeAnalyticsProperties, sanitizePostHogCapture } from './analytics-sanitizer'
import { startAuthOperation } from './auth-telemetry'
import { filterExceptionEvent } from './error-reporting'
import { disableGa4, enableGa4, resolveGa4MeasurementId, trackGa4Event } from './ga4'
import { clearCrossDomainPostHogBootstrap, commitCrossDomainPostHogBootstrap, isTrustedAnalyticsSurfaceHostname, peekCrossDomainPostHogBootstrap, sharesAnalyticsRootDomain, snapshotCrossDomainPostHogBootstrap } from './posthog-handoff-bootstrap'
import { clearDeferredOAuthLoginStarted, deferOAuthLoginStarted, takeDeferredOAuthLoginStarted } from './posthog-oauth-intent'
import { sanitizeAnalyticsPath } from './posthog-privacy'
import { buildUserPersonProperties } from './posthog-user'

const posthog = typeof posthogDefault.capture === 'function' ? posthogDefault : posthogNamed

interface CaptureOptions {
  send_instantly?: boolean
  /** Event time when it differs from now (errors buffered before consent). */
  timestamp?: Date
}

type QueuedAction
  = | { type: 'capture', eventName: string, properties: Record<string, unknown>, options?: CaptureOptions }
    | { type: 'identify', user: AnalyticsUser }
    | { type: 'reset' }

const MAX_QUEUED_ACTIONS = 200
const eventQueue: QueuedAction[] = []
const pendingOwnedAnchorHrefs = new WeakMap<HTMLAnchorElement, {
  originalHref: string
  injectedHref: string
}>()
let isPostHogReady = false
let isPostHogInitialized = false
let analyticsConsent = readAnalyticsConsent()
let currentAnalyticsUser: AnalyticsUser | undefined
let hasCapturedInitialPageview = false
let isAuthBootstrapPending = true
let historyTrackingRestoreScheduled = false
let pendingHistoryCatchupPathname: string | undefined

// Errors raised before analytics consent is decided, or before PostHog is ready,
// are held in memory only (nothing leaves the page) and sent once consent is
// granted, with their original time. Denying consent drops them.
const BUFFERED_ERROR_LIMIT = 50
type BufferedError
  = | { kind: 'event', eventName: string, properties: Partial<EventProperties>, at: number }
    | { kind: 'exception', error: unknown, properties: Record<string, unknown>, at: number }
const bufferedErrors: BufferedError[] = []
const ANALYTICS_ENVIRONMENT = resolveAnalyticsEnvironment((import.meta.env ?? {}) as Record<string, unknown>)
// GA4 has no environment dimension, so only production builds report to it.
const ACTIVE_GA4_MEASUREMENT_ID = ANALYTICS_ENVIRONMENT === 'production' ? resolveGa4MeasurementId(GA4_MEASUREMENT_ID) : ''

if (analyticsConsent !== true) {
  clearPostHogPersistence(POSTHOG_API_KEY)
  disableGa4(ACTIVE_GA4_MEASUREMENT_ID)
  clearDeferredOAuthLoginStarted()
}
snapshotCrossDomainPostHogBootstrap(analyticsConsent)

const POSTHOG_PERSONAL_QUERY_KEYS = [
  'access',
  'auth',
  'code',
  'company',
  'credential',
  'email',
  'first_name',
  'key',
  'last_name',
  'name',
  'organization',
  'password',
  'phone',
  'prefill',
  'refresh',
  'secret',
  'state',
  'sw_ph_distinct_id',
  'sw_ph_session_id',
  'token',
] as const

export function initializePostHogClient(): void {
  if (isPostHogInitialized || isAuthBootstrapPending || analyticsConsent !== true || !POSTHOG_TRACK_EVENTS || !POSTHOG_API_KEY || !POSTHOG_PROJECT_NAME)
    return

  isPostHogInitialized = true
  try {
    const bootstrap = peekCrossDomainPostHogBootstrap(analyticsConsent)
    if (bootstrap) {
      // The SDK applies an anonymous bootstrap on top of existing persistence,
      // but it does not remove a stale `$user_id`. Start from a clean Project
      // persistence so the incoming anonymous ID/session cannot be attributed to a
      // previously signed-in Studio account or discarded by reconciliation.
      clearPostHogPersistence(POSTHOG_API_KEY)
    }
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      ...(bootstrap ? { bootstrap } : {}),
      person_profiles: 'identified_only',
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      save_referrer: true,
      save_campaign_params: true,
      mask_personal_data_properties: true,
      mask_all_text: true,
      custom_personal_data_properties: [...POSTHOG_PERSONAL_QUERY_KEYS],
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      opt_out_capturing_persistence_type: 'cookie',
      capture_pageview: 'history_change',
      capture_pageleave: 'if_capture_pageview',
      enable_heatmaps: true,
      autocapture: {
        dom_event_allowlist: ['click', 'submit'],
        element_allowlist: ['a', 'button', 'form'],
      },
      disable_session_recording: true,
      disable_product_tours: true,
      disable_web_experiments: true,
      disable_conversations: true,
      disable_external_dependency_loading: false,
      // Surveys (onboarding intent) are configured in PostHog and
      // rendered by the SDK. Their targeting is evaluated through the `/flags`
      // endpoint, so that request must stay enabled; we only need it for survey
      // flags, so keep every other flag evaluation off.
      advanced_only_evaluate_survey_feature_flags: true,
      // The SDK otherwise only loads its survey module when the remote config
      // says the project has surveys. That config is cached for hours by our
      // ingestion proxy, so a freshly launched survey would stay invisible until
      // the cache expired. Always load surveys and let /api/surveys decide.
      advanced_enable_surveys: true,
      ip: false,
      // Global fallback for errors nothing else reports. Console errors stay local;
      // frequency and noise are limited in filterExceptionEvent.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      before_send: event => sanitizePostHogCapture(filterExceptionEvent(event), POSTHOG_API_KEY),
      loaded: markPostHogReady,
    })
    if (bootstrap)
      commitCrossDomainPostHogBootstrap(bootstrap)
  }
  catch (error) {
    isPostHogInitialized = false
    if (ANALYTICS_ENVIRONMENT === 'development')
      console.warn('[analytics] PostHog init failed', error)
  }
}

function enqueueAction(action: QueuedAction): void {
  if (eventQueue.length >= MAX_QUEUED_ACTIONS)
    eventQueue.shift()
  eventQueue.push(action)
}

function registerBaseContext(): void {
  posthog.register({
    environment: ANALYTICS_ENVIRONMENT,
    project: POSTHOG_PROJECT_NAME,
  })
}

function optInWithoutAutomaticPageview(): void {
  // The SDK normally emits a pageview inside opt_in_capturing. Pause that one
  // capture so identity reconciliation can finish before our single backfill.
  posthog.set_config({ capture_pageview: false })
  posthog.opt_in_capturing({ captureEventName: false })
}

function restoreHistoryTrackingSoon(initialPageviewPathname?: string): void {
  if (initialPageviewPathname)
    pendingHistoryCatchupPathname = initialPageviewPathname
  if (historyTrackingRestoreScheduled)
    return
  historyTrackingRestoreScheduled = true
  window.setTimeout(() => {
    historyTrackingRestoreScheduled = false
    const catchupFromPathname = pendingHistoryCatchupPathname
    pendingHistoryCatchupPathname = undefined
    if (analyticsConsent !== true)
      return

    posthog.set_config({ capture_pageview: 'history_change' })
    // Vue can redirect `/` or a protected route while pageview capture is
    // briefly paused during identity reconciliation. PostHog observes that
    // history change but deliberately does not emit it while disabled, so
    // backfill the final route once without replacing native route tracking.
    if (catchupFromPathname && window.location.pathname !== catchupFromPathname)
      posthog.capture('$pageview')
  }, 0)
}

function executeIdentify(user: AnalyticsUser): void {
  if (!user.id)
    return
  posthog.identify(user.id, buildUserPersonProperties(user))
}

function restoreConsentAfterReset(): void {
  if (analyticsConsent !== true)
    return
  optInWithoutAutomaticPageview()
  registerBaseContext()
  restoreHistoryTrackingSoon()
}

function reconcileAnalyticsIdentity(previousUserId?: string): void {
  if (!isPostHogReady || analyticsConsent !== true)
    return

  const persistedUserId = posthog.get_property?.('$user_id')
  const persistedUserState = posthog.get_property?.('$user_state')
  const hasPersistedUser = persistedUserState === 'identified'
    || (typeof persistedUserId === 'string' && persistedUserId.length > 0)

  if (!currentAnalyticsUser?.id) {
    if (hasPersistedUser) {
      posthog.reset()
      restoreConsentAfterReset()
    }
    return
  }

  if ((previousUserId && previousUserId !== currentAnalyticsUser.id)
    || (hasPersistedUser && persistedUserId !== currentAnalyticsUser.id)) {
    posthog.reset()
    restoreConsentAfterReset()
  }
  executeIdentify(currentAnalyticsUser)
}

function executeAction(action: QueuedAction): void {
  if (action.type === 'capture') {
    try {
      posthog.capture(action.eventName, action.properties, action.options)
    }
    catch {
      // Continue flushing later events if the SDK rejects one capture.
    }
    trackGa4Event(ACTIVE_GA4_MEASUREMENT_ID, action.eventName, action.properties)
    return
  }
  if (action.type === 'identify') {
    // Only the latest desired identity may be applied. This prevents queued
    // A -> B account switches from briefly merging B back into A on startup.
    if (currentAnalyticsUser?.id === action.user.id)
      reconcileAnalyticsIdentity()
    return
  }
  posthog.reset()
  optInWithoutAutomaticPageview()
  registerBaseContext()
  restoreHistoryTrackingSoon()
}

function flushEventQueue(): void {
  if (!isPostHogReady || isAuthBootstrapPending || analyticsConsent !== true)
    return
  if (posthog.has_opted_out_capturing?.()) {
    eventQueue.length = 0
    return
  }
  while (eventQueue.length)
    executeAction(eventQueue.shift()!)
}

export function markPostHogReady(): void {
  isPostHogReady = true
  if (analyticsConsent !== true) {
    eventQueue.length = 0
    posthog.opt_out_capturing()
    return
  }
  activateReadyAnalytics()
}

export type EventCategory
  = | 'page_view'
    | 'app_management'
    | 'avatar_management'
    | 'user_action'
    | 'error'
    | 'performance'
    | 'auth'
    | 'payment'
    | 'acquisition'
    | 'navigation'

interface EventProperties {
  category?: EventCategory
  [key: string]: unknown
}

type AuthEventProperties = EventProperties
type PaymentEventProperties = EventProperties

/** Which renderer iframe was involved, and for load failures the step that failed. */
interface AvatarRendererContext {
  stage?: string
  iframeVersion?: string
  iframeUrl?: string
}

interface CheckoutEventContext {
  productType: 'subscription' | 'avatar_units'
  planId?: string
  packageId?: string
  billingCycle?: string
  source?: string
  reason?: string
}

/**
 * Joins the events of one avatar creation on /avatars/create, from the first
 * photo to `avatar_created` or a discard. Owned by
 * `src/composables/preprocess/createFlowTelemetry.ts`.
 */
export interface AvatarCreateFlowContext {
  /** Hyphenated UUID: the analytics sanitizer drops 32+ character opaque ids. */
  createFlowId: string
  /** 1 for the first photo; each re-selected photo in the same flow adds 1. */
  photoSeq: number
  /** The recorded beginning is missing (legacy draft or consent granted mid-creation). */
  restored?: boolean
}

function createFlowProperties(flow: AvatarCreateFlowContext | null | undefined): Record<string, unknown> {
  if (!flow)
    return {}
  return {
    create_flow_id: flow.createFlowId,
    photo_seq: flow.photoSeq,
    ...(flow.restored ? { create_flow_restored: true } : {}),
  }
}

function normalizeEnvironmentName(value: unknown): string | undefined {
  if (typeof value !== 'string')
    return undefined
  const normalized = value.trim().toLowerCase()
  return /^[a-z0-9][a-z0-9_-]{0,31}$/.test(normalized) ? normalized : undefined
}

export function resolveAnalyticsEnvironment(env: Record<string, unknown>): string {
  const environment = normalizeEnvironmentName(env.VITE_ENV)
  return environment && ['production', 'preview', 'test', 'development'].includes(environment)
    ? environment
    : 'development'
}

function canTrack(): boolean {
  return POSTHOG_TRACK_EVENTS
    && Boolean(POSTHOG_PROJECT_NAME)
    && analyticsConsent === true
    && !(isPostHogReady && posthog.has_opted_out_capturing?.())
}

function canBufferErrors(): boolean {
  return POSTHOG_TRACK_EVENTS && Boolean(POSTHOG_PROJECT_NAME) && analyticsConsent !== false
}

function bufferError(entry: BufferedError): void {
  if (canBufferErrors() && bufferedErrors.length < BUFFERED_ERROR_LIMIT)
    bufferedErrors.push(entry)
}

function isErrorEvent(eventName: string, properties: Partial<EventProperties>): boolean {
  return properties.category === 'error' || /_(?:failed|error)$/.test(eventName)
}

function flushBufferedErrors(): void {
  for (const entry of bufferedErrors.splice(0)) {
    if (entry.kind === 'event')
      trackEvent(entry.eventName, { ...entry.properties, timestamp: entry.at }, { timestamp: new Date(entry.at) })
    else
      reportException(entry.error, { ...entry.properties, occurred_at: new Date(entry.at).toISOString(), buffered: true })
  }
}

function isExceptionCaptureActive(): boolean {
  return canTrack() && isPostHogReady && !isAuthBootstrapPending
}

/**
 * Report an error caught by a framework hook (Vue errorHandler, router onError)
 * or raised before PostHog's own exception autocapture is running.
 */
export function reportException(error: unknown, properties: Record<string, unknown> = {}): void {
  // Called from error paths (logger.error, Vue errorHandler); must never throw into them.
  try {
    if (!isExceptionCaptureActive()) {
      bufferError({ kind: 'exception', error, properties, at: Date.now() })
      return
    }
    posthog.captureException(error, properties)
  }
  catch {}
}

/**
 * Cover the window before PostHog starts: its exception autocapture only runs
 * after consent and initialization. Once it runs, these listeners stand down so
 * nothing is reported twice.
 */
export function installEarlyErrorCapture(): void {
  if (typeof window === 'undefined')
    return
  window.addEventListener('error', (event) => {
    if (!isExceptionCaptureActive())
      reportException(event.error ?? event.message, { source: 'window_error' })
  })
  window.addEventListener('unhandledrejection', (event) => {
    if (!isExceptionCaptureActive())
      reportException(event.reason, { source: 'unhandled_rejection' })
  })
}

function emitDebugEvent(eventName: string, properties: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !['development', 'test'].includes(ANALYTICS_ENVIRONMENT))
    return
  window.dispatchEvent(new CustomEvent('spatius:analytics', {
    detail: { event: eventName, properties },
  }))
}

function trackEvent(eventName: string, properties: Partial<EventProperties>, options?: CaptureOptions): boolean {
  try {
    if (!canTrack()) {
      if (analyticsConsent === null && isErrorEvent(eventName, properties))
        bufferError({ kind: 'event', eventName, properties, at: Date.now() })
      return false
    }

    const normalizedProperties = { ...properties }
    for (const field of ['audio_duration', 'duration_ms', 'expire_hours']) {
      if (typeof normalizedProperties[field] === 'number')
        normalizedProperties[field] = Math.floor(normalizedProperties[field] as number)
    }
    const enriched = sanitizeAnalyticsProperties({
      timestamp: Date.now(),
      category: properties.category ?? 'user_action',
      schema_version: 1,
      ...normalizedProperties,
    })

    emitDebugEvent(eventName, enriched)
    if (isPostHogReady && !isAuthBootstrapPending) {
      posthog.capture(eventName, enriched, options)
      trackGa4Event(ACTIVE_GA4_MEASUREMENT_ID, eventName, enriched)
    }
    else {
      enqueueAction({ type: 'capture', eventName, properties: enriched, options })
    }
    return true
  }
  catch {
    // Business actions must remain usable even when analytics is unavailable.
    return false
  }
}

// Support opens as a panel over the current page (`?feedback=…`), so the
// panel view, not the path, names those page views.
const SUPPORT_PANEL_PAGE_NAMES: Record<string, string> = {
  'tickets': 'feedback_list',
  'new-ticket': 'feedback_create',
  'ticket': 'feedback_detail',
}

export function getAnalyticsPageName(path: string, search = typeof window !== 'undefined' ? window.location.search : ''): string {
  const panelView = new URLSearchParams(search).get('feedback')
  if (panelView && SUPPORT_PANEL_PAGE_NAMES[panelView])
    return SUPPORT_PANEL_PAGE_NAMES[panelView]
  const normalizedPath = path.replace(/\/$/, '') || '/'
  const exactNames: Record<string, string> = {
    '/': 'entry',
    '/home': 'home',
    '/login': 'login',
    '/pricing': 'pricing',
    '/apps': 'apps_list',
    '/stats': 'stats',
    '/settings': 'settings',
    '/avatars/library': 'avatars_library',
    '/avatars/custom': 'avatars_custom',
    '/avatars/create': 'avatars_create',
    '/avatars/material-guide': 'avatar_material_guide',
    '/avatars/creations/buy': 'avatar_creations_buy',
  }
  if (exactNames[normalizedPath])
    return exactNames[normalizedPath]
  if (/^\/apps\/[^/]+$/.test(normalizedPath))
    return 'app_detail'
  if (/^\/avatars\/[^/]+$/.test(normalizedPath))
    return 'avatar_edit'
  if (/^\/download\/[^/]+$/.test(normalizedPath))
    return 'avatar_download'
  if (/^\/cli\/auth\/[^/]+$/.test(normalizedPath))
    return 'cli_auth'
  return 'not_found'
}

/** Backfill the initial page once, after the auth identity boundary is known. */
function captureInitialPageview(): string | undefined {
  if (hasCapturedInitialPageview || !isPostHogReady || isAuthBootstrapPending || analyticsConsent !== true)
    return undefined
  hasCapturedInitialPageview = true
  const pathname = window.location.pathname
  posthog.capture('$pageview')
  return pathname
}

function activateReadyAnalytics(): void {
  if (!isPostHogReady || isAuthBootstrapPending || analyticsConsent !== true)
    return
  optInWithoutAutomaticPageview()
  registerBaseContext()
  enableGa4(ACTIVE_GA4_MEASUREMENT_ID)
  reconcileAnalyticsIdentity()
  const initialPageviewPathname = captureInitialPageview()
  restoreHistoryTrackingSoon(initialPageviewPathname)
  flushEventQueue()
  flushBufferedErrors()
}

function trackNavigationClick(
  elementId: string,
  placement?: string,
  target?: string,
  options?: { sendInstantly?: boolean },
): void {
  let targetPath: string | undefined
  let navigationKind: 'internal' | 'external' | 'action' = 'action'
  if (target) {
    try {
      const currentUrl = typeof window !== 'undefined' ? window.location.href : 'https://app.spatius.ai/'
      const targetUrl = new URL(target, currentUrl)
      targetPath = sanitizeAnalyticsPath(targetUrl.pathname)
      navigationKind = typeof window !== 'undefined' && targetUrl.origin === window.location.origin ? 'internal' : 'external'
    }
    catch {
      targetPath = sanitizeAnalyticsPath(target)
    }
  }
  trackEvent('navigation_clicked', {
    category: 'navigation',
    action_type: 'navigation_clicked',
    element_id: elementId,
    placement,
    from_page: typeof window !== 'undefined' ? getAnalyticsPageName(window.location.pathname) : undefined,
    from_path: typeof window !== 'undefined' ? sanitizeAnalyticsPath(window.location.pathname) : undefined,
    target_path: targetPath,
    navigation_kind: navigationKind,
  }, { send_instantly: options?.sendInstantly })
}

function identifyAnalyticsUser(user: AnalyticsUser): void {
  if (!user.id)
    return
  const previousUserId = currentAnalyticsUser?.id
  currentAnalyticsUser = user
  if (!canTrack())
    return
  if (isAuthBootstrapPending)
    return
  if (isPostHogReady)
    reconcileAnalyticsIdentity(previousUserId)
  else
    enqueueAction({ type: 'identify', user })
}

function resetAnalyticsUser(): void {
  currentAnalyticsUser = undefined
  if (isPostHogReady) {
    posthog.reset()
    if (analyticsConsent === true) {
      optInWithoutAutomaticPageview()
      registerBaseContext()
      restoreHistoryTrackingSoon()
    }
  }
  else if (analyticsConsent === true) {
    enqueueAction({ type: 'reset' })
  }
}

function startAuthBootstrap(): void {
  isAuthBootstrapPending = true
}

function finishAuthBootstrap(): void {
  isAuthBootstrapPending = false
  initializePostHogClient()
  activateReadyAnalytics()
}

export function updateAnalyticsConsent(consent: AnalyticsConsent): void {
  const wasGranted = analyticsConsent === true
  analyticsConsent = consent
  writeAnalyticsConsent(consent)

  if (consent !== true) {
    eventQueue.length = 0
    bufferedErrors.length = 0
    hasCapturedInitialPageview = false
    clearDeferredOAuthLoginStarted()
    clearCrossDomainPostHogBootstrap()
    if (isPostHogInitialized || isPostHogReady) {
      posthog.reset(true)
      posthog.opt_out_capturing()
    }
    clearPostHogPersistence(POSTHOG_API_KEY)
    disableGa4(ACTIVE_GA4_MEASUREMENT_ID)
    return
  }

  initializePostHogClient()
  activateReadyAnalytics()

  if (!wasGranted) {
    trackEvent('analytics_consent_granted', {
      category: 'user_action',
      action_type: 'analytics_consent_granted',
    })
  }
}

const ANONYMOUS_DISTINCT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[47][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const POSTHOG_SESSION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function appendCrossDomainIdentityHandoff(rawUrl: string): string {
  if (!isPostHogReady || typeof window === 'undefined')
    return rawUrl
  try {
    const persistedUserId = posthog.get_property?.('$user_id')
    const persistedUserState = posthog.get_property?.('$user_state')
    if (persistedUserState === 'identified' || (typeof persistedUserId === 'string' && persistedUserId))
      return rawUrl
    const target = new URL(rawUrl, window.location.href)
    if (!['http:', 'https:'].includes(target.protocol) || !isTrustedAnalyticsSurfaceHostname(target.hostname))
      return rawUrl
    if (sharesAnalyticsRootDomain(target.hostname, window.location.hostname))
      return rawUrl
    const distinctId = posthog.get_distinct_id?.()
    const sessionId = posthog.get_session_id?.()
    if (!ANONYMOUS_DISTINCT_ID_PATTERN.test(distinctId || '') || !POSTHOG_SESSION_ID_PATTERN.test(sessionId || ''))
      return rawUrl
    const identity = new URLSearchParams({
      sw_ph_distinct_id: distinctId,
      sw_ph_session_id: sessionId,
    })
    target.searchParams.set('sw_source_site', window.location.hostname.toLowerCase())
    const retainedHashSegments = target.hash.slice(1).split('&').filter((segment) => {
      const rawKey = segment.split('=', 1)[0]
      try {
        const key = decodeURIComponent(rawKey.replace(/\+/g, ' '))
        return key !== 'sw_handoff' && key !== 'sw_ph_distinct_id' && key !== 'sw_ph_session_id'
      }
      catch {
        return true
      }
    }).filter(Boolean)
    retainedHashSegments.push(`sw_handoff=${encodeURIComponent(identity.toString())}`)
    target.hash = retainedHashSegments.join('&')
    return target.toString()
  }
  catch {
    return rawUrl
  }
}

export function installAnalyticsClickTracking(): () => void {
  if (typeof document === 'undefined')
    return () => {}
  const handleActivation = (event: MouseEvent) => {
    if ((event.type === 'click' && event.button !== 0) || (event.type === 'auxclick' && event.button !== 1))
      return
    const target = event.target
    if (!(target instanceof Element))
      return
    const clickedAnchor = target.closest<HTMLAnchorElement>('a[href]')
    if (clickedAnchor && !clickedAnchor.hasAttribute('download') && analyticsConsent === true && typeof window !== 'undefined') {
      const pending = pendingOwnedAnchorHrefs.get(clickedAnchor)
      const originalHref = pending && clickedAnchor.href === pending.injectedHref
        ? pending.originalHref
        : clickedAnchor.href
      if (pending && clickedAnchor.href !== pending.injectedHref)
        pendingOwnedAnchorHrefs.delete(clickedAnchor)

      const injectedHref = appendCrossDomainIdentityHandoff(originalHref)
      if (injectedHref !== originalHref) {
        clickedAnchor.href = injectedHref
        const nextPending = { originalHref, injectedHref }
        pendingOwnedAnchorHrefs.set(clickedAnchor, nextPending)
        window.setTimeout(() => {
          if (pendingOwnedAnchorHrefs.get(clickedAnchor) !== nextPending)
            return
          if (clickedAnchor.href === injectedHref)
            clickedAnchor.href = originalHref
          pendingOwnedAnchorHrefs.delete(clickedAnchor)
        }, 0)
      }
    }
    const analyticsElement = target.closest<HTMLElement>('[data-analytics-id]')
    if (!analyticsElement || analyticsElement.getAttribute('aria-disabled') === 'true' || analyticsElement.hasAttribute('disabled'))
      return
    const elementId = analyticsElement.dataset.analyticsId
    if (!elementId)
      return
    const anchor = analyticsElement.closest<HTMLAnchorElement>('a[href]')
    if (event.type === 'auxclick' && !anchor)
      return
    const destination = analyticsElement.dataset.analyticsTarget || anchor?.href
    trackNavigationClick(elementId, analyticsElement.dataset.analyticsPlacement, destination, {
      sendInstantly: Boolean(anchor?.target && anchor.target !== '_self')
        || Boolean(destination && !destination.startsWith(window.location.origin)),
    })
  }
  document.addEventListener('click', handleActivation, true)
  document.addEventListener('auxclick', handleActivation, true)
  return () => {
    document.removeEventListener('click', handleActivation, true)
    document.removeEventListener('auxclick', handleActivation, true)
  }
}

/**
 * Surveys are `api`-type: PostHog decides who is eligible (targeting, "seen once")
 * and Studio renders them. These helpers are the whole bridge — ask the SDK for
 * an eligible survey, then report shown / sent / dismissed exactly as posthog-js
 * would, including the person properties and localStorage marker its own
 * eligibility check reads, so a survey answered here never comes back.
 */
interface SurveyLike {
  id: string
  name: string
  questions: unknown[]
}

function waitForPostHogReady(timeoutMs = 20_000): Promise<boolean> {
  if (isPostHogReady)
    return Promise.resolve(true)
  return new Promise((resolve) => {
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      if (isPostHogReady) {
        window.clearInterval(timer)
        resolve(true)
      }
      else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer)
        resolve(false)
      }
    }, 500)
  })
}

/**
 * The named survey, if PostHog considers it active for this user right now:
 * running, targeting flag on, not yet answered or dismissed, URL and trigger event
 * conditions met. Null otherwise, and when analytics is off (no consent, no key).
 */
export async function getActiveSurvey<T extends SurveyLike>(surveyName: string): Promise<T | null> {
  if (!(await waitForPostHogReady()))
    return null
  return new Promise((resolve) => {
    posthog.getActiveMatchingSurveys((surveys) => {
      resolve((surveys.find(item => item.name === surveyName) as unknown as T) ?? null)
    }, false)
  })
}

/**
 * Development-only: the named survey regardless of eligibility, so its dialog can
 * be checked without registering. Never used in production builds.
 */
export async function getSurveyForPreview<T extends SurveyLike>(surveyName: string): Promise<T | null> {
  if (ANALYTICS_ENVIRONMENT === 'production')
    return null
  if (!(await waitForPostHogReady())) {
    console.warn(`[analytics] cannot preview survey "${surveyName}": PostHog did not initialize within 20s (cookie consent rejected, or VITE_POSTHOG_API_KEY missing)`)
    return null
  }
  return new Promise((resolve) => {
    posthog.getSurveys((surveys) => {
      const survey = surveys.find(item => item.name === surveyName)
      if (!survey)
        console.warn(`[analytics] survey "${surveyName}" is not in the active survey list`, surveys.map(item => item.name))
      resolve((survey as unknown as T) ?? null)
    }, true)
  })
}

function markSurveySeen(surveyId: string): void {
  try {
    window.localStorage.setItem(`seenSurvey_${surveyId}`, 'true')
  }
  catch {
    // Storage blocked: PostHog's person property still stops the repeat.
  }
}

export function captureSurveyShown(survey: SurveyLike): void {
  if (!isPostHogReady)
    return
  posthog.capture('survey shown', { $survey_id: survey.id, $survey_name: survey.name })
}

export function captureSurveySent(survey: SurveyLike, properties: Record<string, unknown>): void {
  if (!isPostHogReady)
    return
  posthog.capture('survey sent', {
    ...properties,
    $set: { [`$survey_responded/${survey.id}`]: true },
  })
  markSurveySeen(survey.id)
}

export function captureSurveyDismissed(survey: SurveyLike): void {
  if (!isPostHogReady)
    return
  posthog.capture('survey dismissed', {
    $survey_id: survey.id,
    $survey_name: survey.name,
    $set: { [`$survey_dismissed/${survey.id}`]: true },
  })
  markSurveySeen(survey.id)
}

export const PostHogTracker = {
  navigation: {
    clicked: trackNavigationClick,
  },

  usage: {
    tabViewed: (usageTab: 'overview' | 'session-history' | 'avatar-creations' | 'credits'): boolean => {
      return trackEvent('usage_tab_viewed', {
        category: 'page_view',
        action_type: 'usage_tab_viewed',
        usage_tab: usageTab,
      })
    },
  },

  /**
   * 应用管理追踪
   */
  app: {
    creationStarted: (source: string): void => {
      trackEvent('app_creation_started', {
        category: 'app_management',
        action_type: 'app_creation_started',
        source,
      })
    },

    /**
     * 创建应用
     */
    created: (appId: string, appName: string): void => {
      trackEvent('app_created', {
        category: 'app_management',
        action_type: 'app_created',
        app_id: appId,
        app_name: appName,
      })
    },

    /**
     * 删除应用
     */
    deleted: (appId: string, appName: string): void => {
      trackEvent('app_deleted', {
        category: 'app_management',
        action_type: 'app_deleted',
        app_id: appId,
        app_name: appName,
      })
    },

    /**
     * 查看应用详情
     */
    viewed: (appId: string): void => {
      trackEvent('app_viewed', {
        category: 'app_management',
        action_type: 'app_viewed',
        app_id: appId,
      })
    },

    /**
     * 创建 API Key
     */
    apiKeyCreated: (appId: string, keyName: string): void => {
      trackEvent('api_key_created', {
        category: 'app_management',
        action_type: 'api_key_created',
        app_id: appId,
        key_name: keyName,
      })
    },

    /**
     * 删除 API Key
     */
    apiKeyDeleted: (appId: string): void => {
      trackEvent('api_key_deleted', {
        category: 'app_management',
        action_type: 'api_key_deleted',
        app_id: appId,
      })
    },

    /**
     * 复制 API Key
     */
    apiKeyCopied: (appId: string): void => {
      trackEvent('api_key_copied', {
        category: 'app_management',
        action_type: 'api_key_copied',
        app_id: appId,
      })
    },

    /**
     * 生成 Session Token
     */
    sessionTokenGenerated: (appId: string, expireHours: number): void => {
      trackEvent('session_token_generated', {
        category: 'app_management',
        action_type: 'session_token_generated',
        app_id: appId,
        expire_hours: expireHours,
      })
    },

    /**
     * 复制 Session Token
     */
    sessionTokenCopied: (appId: string): void => {
      trackEvent('session_token_copied', {
        category: 'app_management',
        action_type: 'session_token_copied',
        app_id: appId,
      })
    },

    credentialsViewed: (appId: string, source: string): void => {
      trackEvent('app_credentials_viewed', {
        category: 'app_management',
        action_type: 'app_credentials_viewed',
        app_id: appId,
        source,
      })
    },

    credentialCopied: (appId: string, credentialType: 'app_name' | 'app_id' | 'api_key' | 'env', source: string): void => {
      trackEvent('credential_copied', {
        category: 'app_management',
        action_type: 'credential_copied',
        app_id: appId,
        credential_type: credentialType,
        source,
      })
    },
  },

  /**
   * Avatar 管理追踪
   */
  avatar: {
    /**
     * 开始创建 Avatar：每选一张照片报一次（同一次创建里换照片时 photo_seq 加 1）
     */
    creationStarted: (flow?: AvatarCreateFlowContext | null): void => {
      trackEvent('avatar_creation_started', {
        category: 'avatar_management',
        action_type: 'avatar_creation_started',
        ...createFlowProperties(flow),
      })
    },

    creationBlocked: (availableUnits: number, requiredUnits: number): boolean => {
      return trackEvent('avatar_creation_blocked', {
        category: 'avatar_management',
        action_type: 'avatar_creation_blocked',
        reason: 'insufficient_avatar_creations',
        available_units: availableUnits,
        required_units: requiredUnits,
      })
    },

    /**
     * 上传图片：照片已上传、后台预处理任务已建立（每张照片一次）
     */
    imageUploaded: (properties: {
      flow: AvatarCreateFlowContext | null
      taskId: string
      fileSize: number
      fileType: string
      width?: number
      height?: number
      elapsedMs?: number
    }): void => {
      trackEvent('avatar_image_uploaded', {
        category: 'avatar_management',
        action_type: 'avatar_image_uploaded',
        ...createFlowProperties(properties.flow),
        task_id: properties.taskId,
        file_size: properties.fileSize,
        file_type: properties.fileType,
        width: properties.width,
        height: properties.height,
        elapsed_ms: properties.elapsedMs,
      })
    },

    /**
     * 图片预处理完成（产出可用于生成的最终图，进入填写名字这一步）
     */
    preprocessCompleted: (properties: {
      flow: AvatarCreateFlowContext | null
      taskId?: string | null
      attemptSeq?: number
      chosenVariant?: string
      needsCrop?: boolean
      needsAdjust?: boolean
      rawActions?: string[]
      elapsedMs?: number
    }): void => {
      trackEvent('avatar_preprocess_completed', {
        category: 'avatar_management',
        action_type: 'avatar_preprocess_completed',
        ...createFlowProperties(properties.flow),
        task_id: properties.taskId ?? undefined,
        attempt_seq: properties.attemptSeq,
        chosen_variant: properties.chosenVariant,
        needs_crop: properties.needsCrop,
        needs_adjust: properties.needsAdjust,
        raw_actions: properties.rawActions,
        elapsed_ms: properties.elapsedMs,
      })
    },

    /**
     * 照片被拒：上传前检查不通过（upload-invalid）或后台检测拒绝（detect-reject）
     */
    photoRejected: (properties: {
      flow: AvatarCreateFlowContext | null
      taskId?: string | null
      source: string
      reason: string
      elapsedMs?: number
    }): void => {
      trackEvent('avatar_photo_rejected', {
        category: 'avatar_management',
        action_type: 'avatar_photo_rejected',
        ...createFlowProperties(properties.flow),
        task_id: properties.taskId ?? undefined,
        source: properties.source,
        reason: properties.reason,
        elapsed_ms: properties.elapsedMs,
      })
    },

    /**
     * 换照片 / 重新开始 / 放弃：用户放下当前照片时报一次。
     * from_step 是当时所在的步骤（预处理视图，下划线形式）；
     * reason 是 after_reject / after_failure / user_reselect / discard。
     */
    creationRestarted: (properties: {
      flow: AvatarCreateFlowContext | null
      fromStep: string
      reason: string
    }): void => {
      trackEvent('avatar_creation_restarted', {
        category: 'avatar_management',
        action_type: 'avatar_creation_restarted',
        ...createFlowProperties(properties.flow),
        from_step: properties.fromStep,
        reason: properties.reason,
      })
    },

    /**
     * 开始从预处理完成的图片生成 Avatar
     */
    generationStarted: (metadata?: {
      needsCrop?: boolean
      needsAdjust?: boolean
      taskId?: string
      avatarEmotion?: string
      pipelineVersion?: string
      selectionSource?: 'user' | 'legacy'
    }, flow?: AvatarCreateFlowContext | null): void => {
      trackEvent('avatar_generation_started', {
        category: 'avatar_management',
        action_type: 'avatar_generation_started',
        ...createFlowProperties(flow),
        needs_crop: metadata?.needsCrop,
        needs_adjust: metadata?.needsAdjust,
        task_id: metadata?.taskId,
        selected_emotion: metadata?.avatarEmotion,
        pipeline_version: metadata?.pipelineVersion,
        selection_source: metadata?.selectionSource,
      })
    },

    /**
     * 裁剪图片
     */
    imageCropped: (cropRatio: string): void => {
      trackEvent('avatar_image_cropped', {
        category: 'avatar_management',
        action_type: 'avatar_image_cropped',
        crop_ratio: cropRatio,
      })
    },

    /**
     * Avatar 创建成功（后台已受理，avatar_id 是后台请求编号）
     */
    created: (avatarId: string, avatarName: string, enableAiProcessing: boolean, flow?: AvatarCreateFlowContext | null): void => {
      trackEvent('avatar_created', {
        category: 'avatar_management',
        action_type: 'avatar_created',
        ...createFlowProperties(flow),
        avatar_id: avatarId,
        avatar_name: avatarName,
        enable_ai_processing: enableAiProcessing,
      })
    },

    /**
     * Avatar 创建失败（最终提交）。
     * phase 是失败环节：precheck（额度预检）/ upload（最终图上传）/ create（创建接口）；
     * result_unknown 表示创建接口结果不明（可能已创建），页面会挡住再次提交。
     */
    creationFailed: (errorType: string, errorMessage: string, context: {
      flow?: AvatarCreateFlowContext | null
      phase?: 'precheck' | 'upload' | 'create'
      resultUnknown?: boolean
    } = {}): void => {
      trackEvent('avatar_creation_failed', {
        category: 'avatar_management',
        action_type: 'avatar_creation_failed',
        ...createFlowProperties(context.flow),
        error_type: errorType,
        error_message: errorMessage,
        phase: context.phase,
        result_unknown: context.resultUnknown,
      })
    },

    /**
     * 查看 Avatar 库（公开）
     */
    libraryViewed: (avatarCount: number): void => {
      trackEvent('avatar_library_viewed', {
        category: 'avatar_management',
        action_type: 'avatar_library_viewed',
        avatar_count: avatarCount,
      })
    },

    /**
     * 查看 Custom Avatars 列表
     */
    personalListViewed: (avatarCount: number, filterStatus: 'all' | 'success' | 'generating' | 'failure'): void => {
      trackEvent('personal_avatars_viewed', {
        category: 'avatar_management',
        action_type: 'personal_avatars_viewed',
        avatar_count: avatarCount,
        filter_status: filterStatus,
      })
    },

    /**
     * 点击 Avatar 卡片
     */
    cardClicked: (avatarId: string, source: 'library' | 'personal' | 'other'): void => {
      trackEvent('avatar_card_clicked', {
        category: 'avatar_management',
        action_type: 'avatar_card_clicked',
        avatar_id: avatarId,
        source,
      })
    },

    /**
     * 复制 Avatar ID
     */
    idCopied: (avatarId: string): void => {
      trackEvent('avatar_id_copied', {
        category: 'avatar_management',
        action_type: 'avatar_id_copied',
        avatar_id: avatarId,
      })
    },

    /**
     * 进入 Avatar 编辑页
     */
    editPageViewed: (avatarId: string): void => {
      trackEvent('avatar_edit_page_viewed', {
        category: 'avatar_management',
        action_type: 'avatar_edit_page_viewed',
        avatar_id: avatarId,
      })
    },

    /**
     * 完成演示配置（首次选择或后续修改；skipped = 点了"使用默认设置"）
     */
    demoSetupCompleted: (avatarId: string, voiceGender: string, roleType: string, skipped = false): void => {
      trackEvent('avatar_demo_setup_completed', {
        category: 'avatar_management',
        action_type: 'avatar_demo_setup_completed',
        avatar_id: avatarId,
        setup_mode: skipped ? 'default' : 'selected',
        voice_gender: voiceGender,
        role_type: roleType,
      })
    },

    /**
     * 效果评分面板在对话结束后弹出了（每个 Avatar 每个浏览器一次）。
     * 和 effectFeedbackSubmitted 一起算作答率。
     */
    effectFeedbackPrompted: (avatarId: string): void => {
      trackEvent('avatar_effect_feedback_prompted', {
        category: 'avatar_management',
        action_type: 'avatar_effect_feedback_prompted',
        avatar_id: avatarId,
      })
    },

    /**
     * 当前 Avatar 效果反馈（0～10 评分；低分附问题项与备注）。面板关闭时把最终结果
     * 发一条，中间改分不发。按 avatar_name 做 breakdown 就是每个角色的打分图。
     */
    effectFeedbackSubmitted: (
      avatarId: string,
      score: number,
      issues?: string[],
      note?: string,
      context?: { avatarName?: string, durationSeconds: number | null, endReason?: string },
    ): void => {
      trackEvent('avatar_effect_feedback_submitted', {
        category: 'avatar_management',
        action_type: 'avatar_effect_feedback_submitted',
        avatar_id: avatarId,
        // 报表 breakdown 用名字，id 留作精确匹配
        avatar_name: context?.avatarName,
        score,
        issues,
        note,
        // 评的是哪次对话：连上之后聊了多久、怎么结束的
        conversation_duration_seconds: context?.durationSeconds ?? undefined,
        conversation_end_reason: context?.endReason,
      })
    },

    /**
     * 打开申请公开授权弹窗（source：high_score_invite = 高分邀请）
     */
    publicApplyDialogOpened: (avatarId: string, source: 'high_score_invite' | 'delivery_link'): void => {
      trackEvent('avatar_public_apply_dialog_opened', {
        category: 'avatar_management',
        action_type: 'avatar_public_apply_dialog_opened',
        avatar_id: avatarId,
        source,
      })
    },

    /**
     * 提交申请公开（工单已创建）
     */
    publicApplySubmitted: (avatarId: string): void => {
      trackEvent('avatar_public_apply_submitted', {
        category: 'avatar_management',
        action_type: 'avatar_public_apply_submitted',
        avatar_id: avatarId,
      })
    },

    /**
     * 更新 Avatar 配置
     */
    configUpdated: (avatarId: string, configType: 'name' | 'voice' | 'dpt_provider' | 'other', changes?: Record<string, unknown>): void => {
      trackEvent('avatar_config_updated', {
        category: 'avatar_management',
        action_type: 'avatar_config_updated',
        avatar_id: avatarId,
        config_type: configType,
        ...changes,
      })
    },

    /**
     * 更新后处理参数
     */
    postprocessUpdated: (avatarId: string, parameters: string[]): void => {
      trackEvent('avatar_postprocess_updated', {
        category: 'avatar_management',
        action_type: 'avatar_postprocess_updated',
        avatar_id: avatarId,
        parameters,
      })
    },

    /**
     * 保存 Avatar 修改
     */
    saved: (avatarId: string, hasConfigChanges: boolean, hasPostprocessChanges: boolean): void => {
      trackEvent('avatar_saved', {
        category: 'avatar_management',
        action_type: 'avatar_saved',
        avatar_id: avatarId,
        has_config_changes: hasConfigChanges,
        has_postprocess_changes: hasPostprocessChanges,
      })
    },

    /**
     * 删除 Avatar
     */
    deleted: (avatarId: string): void => {
      trackEvent('avatar_deleted', {
        category: 'avatar_management',
        action_type: 'avatar_deleted',
        avatar_id: avatarId,
      })
    },

    /**
     * 复制/克隆 Avatar
     */
    duplicated: (sourceAvatarId: string, newAvatarId?: string): void => {
      trackEvent('avatar_duplicated', {
        category: 'avatar_management',
        action_type: 'avatar_duplicated',
        source_avatar_id: sourceAvatarId,
        avatar_id: newAvatarId,
        new_avatar_id_available: Boolean(newAvatarId),
      })
    },

    /**
     * 开始预览 Avatar
     */
    previewStarted: (avatarId: string, previewMode: 'idle' | 'conversation'): void => {
      trackEvent('avatar_preview_started', {
        category: 'avatar_management',
        action_type: 'avatar_preview_started',
        avatar_id: avatarId,
        preview_mode: previewMode,
      })
    },

    /**
     * 录制音频用于预览
     * @param avatarId 角色ID
     * @param audioDuration 音频时长（秒），会自动转换为整数
     */
    audioRecorded: (avatarId: string, audioDuration: number): void => {
      trackEvent('avatar_audio_recorded', {
        category: 'avatar_management',
        action_type: 'avatar_audio_recorded',
        avatar_id: avatarId,
        audio_duration: audioDuration, // trackEvent 会自动转换为整数
      })
    },

    /**
     * 上传音频用于预览
     */
    audioUploaded: (avatarId: string, fileSize: number, fileType: string): void => {
      trackEvent('avatar_audio_uploaded', {
        category: 'avatar_management',
        action_type: 'avatar_audio_uploaded',
        avatar_id: avatarId,
        file_size: fileSize,
        file_type: fileType,
      })
    },

    /**
     * 播放音频预览
     * @param avatarId 角色ID
     * @param audioSource 音频来源
     * @param reqId 请求ID，用于关联请求和响应
     */
    audioPlayed: (avatarId: string, audioSource: 'recorded' | 'uploaded' | 'upload_list', reqId?: string): void => {
      trackEvent('avatar_audio_played', {
        category: 'avatar_management',
        action_type: 'avatar_audio_played',
        avatar_id: avatarId,
        audio_source: audioSource,
        req_id: reqId,
      })
    },

    realtimeConnectionStarted: (avatarId: string): void => {
      trackEvent('avatar_preview_realtime_connection_started', {
        category: 'avatar_management',
        action_type: 'avatar_preview_realtime_connection_started',
        avatar_id: avatarId,
      })
    },

    realtimeConnected: (avatarId: string): void => {
      trackEvent('avatar_preview_realtime_connected', {
        category: 'avatar_management',
        action_type: 'avatar_preview_realtime_connected',
        avatar_id: avatarId,
      })
    },

    /**
     * 已连上的对话里某个操作失败（不中断会话）
     * @param avatarId 角色ID
     * @param action toggle_microphone / send_text / greeting
     * @param errorMessage 原始错误信息
     */
    realtimeActionFailed: (avatarId: string, action: 'toggle_microphone' | 'send_text' | 'greeting', errorMessage: string): void => {
      trackEvent('avatar_preview_realtime_action_failed', {
        category: 'error',
        action_type: 'avatar_preview_realtime_action_failed',
        avatar_id: avatarId,
        action,
        error_message: errorMessage,
      })
    },

    realtimeConnectionFailed: (avatarId: string, reason: string): void => {
      trackEvent('avatar_preview_realtime_connection_failed', {
        category: 'avatar_management',
        action_type: 'avatar_preview_realtime_connection_failed',
        avatar_id: avatarId,
        reason,
      })
    },

    realtimeDisconnected: (avatarId: string): void => {
      trackEvent('avatar_preview_realtime_disconnected', {
        category: 'avatar_management',
        action_type: 'avatar_preview_realtime_disconnected',
        avatar_id: avatarId,
      })
    },

    realtimeSessionStarted: (avatarId: string, reqId?: string): void => {
      trackEvent('avatar_preview_realtime_session_started', {
        category: 'avatar_management',
        action_type: 'avatar_preview_realtime_session_started',
        avatar_id: avatarId,
        req_id: reqId,
      })
    },

    realtimeSessionCompleted: (avatarId: string, endReason = 'conversation_ended', reqId?: string, durationSeconds?: number): void => {
      trackEvent('avatar_preview_realtime_session_completed', {
        category: 'avatar_management',
        action_type: 'avatar_preview_realtime_session_completed',
        avatar_id: avatarId,
        duration_seconds: durationSeconds,
        req_id: reqId,
        end_reason: endReason,
      })
    },

    realtimeSessionFailed: (avatarId: string, reason: string, reqId?: string): void => {
      trackEvent('avatar_preview_realtime_session_failed', {
        category: 'avatar_management',
        action_type: 'avatar_preview_realtime_session_failed',
        avatar_id: avatarId,
        req_id: reqId,
        reason,
      })
    },

    /**
     * 发起下载资产包
     */
    downloadInitiated: (avatarId: string, source: 'library' | 'personal'): void => {
      trackEvent('avatar_download_initiated', {
        category: 'avatar_management',
        action_type: 'avatar_download_initiated',
        avatar_id: avatarId,
        source,
      })
    },

    /**
     * 成功下载资产包
     */
    downloadCompleted: (avatarId: string, qualities: string[], downloadSize?: number): void => {
      trackEvent('avatar_download_completed', {
        category: 'avatar_management',
        action_type: 'avatar_download_completed',
        avatar_id: avatarId,
        qualities,
        download_size: downloadSize,
      })
    },

    /**
     * 下载失败
     */
    downloadFailed: (avatarId: string, errorType: string): void => {
      trackEvent('avatar_download_failed', {
        category: 'avatar_management',
        action_type: 'avatar_download_failed',
        avatar_id: avatarId,
        error_type: errorType,
      })
    },

    /**
     * 角色加载失败
     * @param avatarId 角色ID
     * @param errorType 错误类型：missing_camera_config, missing_character_settings, sdk_error, etc.
     * @param errorMessage 原始错误信息（非界面文案）
     * @param context 失败所在步骤与所用 iframe 版本
     */
    loadFailed: (avatarId: string, errorType: string, errorMessage: string, context: AvatarRendererContext = {}): void => {
      trackEvent('avatar_load_failed', {
        category: 'avatar_management',
        action_type: 'avatar_load_failed',
        avatar_id: avatarId,
        error_type: errorType,
        error_message: errorMessage,
        stage: context.stage,
        iframe_version: context.iframeVersion,
        iframe_url: context.iframeUrl,
      })
    },

    /**
     * 渲染器 iframe 加载完成后的错误
     * @param avatarId 角色ID
     * @param errorType renderer_message：iframe 主动上报（无 reqId）；command_failed：命令报错或超时
     * @param errorMessage 原始错误信息
     * @param context 失败的命令与所用 iframe 版本
     */
    rendererError: (avatarId: string, errorType: 'renderer_message' | 'command_failed', errorMessage: string, context: AvatarRendererContext & { command?: string } = {}): void => {
      trackEvent('avatar_renderer_error', {
        category: 'avatar_management',
        action_type: 'avatar_renderer_error',
        avatar_id: avatarId,
        error_type: errorType,
        error_message: errorMessage,
        command: context.command,
        iframe_version: context.iframeVersion,
        iframe_url: context.iframeUrl,
      })
    },

    /**
     * 音频发送失败
     * @param avatarId 角色ID
     * @param errorType 错误类型：controller_not_available, send_to_server_failed, unknown_error
     * @param errorMessage 错误信息
     * @param reqId 请求ID
     */
    audioSendFailed: (avatarId: string, errorType: string, errorMessage: string, reqId?: string): void => {
      trackEvent('avatar_audio_send_failed', {
        category: 'avatar_management',
        action_type: 'avatar_audio_send_failed',
        avatar_id: avatarId,
        error_type: errorType,
        error_message: errorMessage,
        req_id: reqId,
      })
    },
  },

  /**
   * 用户行为追踪
   */
  user: {
    authBootstrapStarted: startAuthBootstrap,
    authBootstrapResolved: finishAuthBootstrap,
    identify: identifyAnalyticsUser,
    reset: resetAnalyticsUser,
  },

  error: {
    /**
     * 通用错误
     */
    occurred: (errorType: string, errorMessage: string, pageName?: string, userAction?: string, context?: Record<string, unknown>): void => {
      trackEvent('error_occurred', {
        category: 'error',
        error_type: errorType,
        error_message: errorMessage,
        page_name: pageName,
        user_action: userAction,
        ...context,
      })
    },

    /**
     * API 请求失败（useApi 统一出口；401 与主动取消不报）
     * @param properties method / path / status / error_code / error_name / failure_kind / notified（不含后端原文）
     */
    apiRequestFailed: (properties: Record<string, unknown>): void => {
      trackEvent('api_request_failed', {
        category: 'error',
        error_type: 'api_request_failed',
        ...properties,
      })
    },

    /**
     * 创建页预处理系统错误：后台任务失败（backend_failed）或前端本地错误（local_error，如上传失败）。
     * reason 在 backend_failed 时是失败原因枚举（TrackFailReason），在 local_error 时是本地错误枚举；
     * phase 是出错前所在的步骤（预处理视图，下划线形式）。
     */
    preprocessSystemError: (properties: {
      flow: AvatarCreateFlowContext | null
      kind: 'backend_failed' | 'local_error'
      reason: string
      terminationCode?: string
      retryable: boolean
      phase?: string
      taskId?: string | null
      attemptSeq?: number
      elapsedMs?: number
    }): void => {
      trackEvent('avatar_preprocess_system_error', {
        category: 'error',
        error_type: 'avatar_preprocess_system_error',
        ...createFlowProperties(properties.flow),
        kind: properties.kind,
        reason: properties.reason,
        termination_code: properties.terminationCode,
        retryable: properties.retryable,
        phase: properties.phase,
        task_id: properties.taskId ?? undefined,
        attempt_seq: properties.attemptSeq,
        elapsed_ms: properties.elapsedMs,
      })
    },
  },

  /**
   * 性能指标追踪
   */
  performance: {
    /**
     * 页面加载时间
     * @param pageName 页面名称
     * @param durationMs 页面加载时长（毫秒），会自动转换为整数
     */
    pageLoad: (pageName: string, durationMs: number): void => {
      trackEvent('performance_page_load', {
        category: 'performance',
        metric_type: 'page_load',
        page_name: pageName,
        duration_ms: durationMs, // trackEvent 会自动转换为整数
      })
    },

    /**
     * studio-rtc 遥测：每轮 rtc_playback_stats、每会话 rtc_session_summary（performance），
     * 以及连接层错误 / 重连 / 断流事件（error）。事件名与 web-rtc 同名，范围见 src/utils/rtc-playback-report.ts
     * @param event 事件名
     * @param properties 事件字段
     */
    rtcTelemetry: (event: string, properties: Record<string, unknown>): void => {
      const performanceEvent = event === 'rtc_playback_stats' || event === 'rtc_session_summary'
      trackEvent(event, {
        category: performanceEvent ? 'performance' : 'error',
        metric_type: event,
        ...properties,
      })
    },

    /**
     * 创建页后台处理等太久：同一步骤（processing / finalizing）连续等待超过阈值时，每个任务每步报一次。
     * duration_ms 是已等待毫秒数，会自动转换为整数。
     */
    preprocessTerminalWaitSlow: (properties: {
      flow: AvatarCreateFlowContext | null
      taskId: string
      phase: string
      durationMs: number
      attemptSeq?: number
    }): void => {
      trackEvent('avatar_preprocess_terminal_wait_slow', {
        category: 'performance',
        metric_type: 'avatar_preprocess_terminal_wait_slow',
        ...createFlowProperties(properties.flow),
        task_id: properties.taskId,
        phase: properties.phase,
        duration_ms: properties.durationMs,
        attempt_seq: properties.attemptSeq,
      })
    },
  },

  /**
   * 认证追踪
   */
  auth: {
    operationStarted: (input: AuthOperationInput): AuthOperationRecorder => {
      return startAuthOperation(input, (event) => {
        trackEvent('auth_operation_completed', {
          category: event.outcome === 'failure' ? 'error' : 'auth',
          ...event,
        }, { send_instantly: event.stage === 'oauth_start' })
      })
    },

    attemptStarted: (method: 'email' | 'oauth_github' | 'oauth_google', intent: 'login' | 'signup' | 'login_or_signup'): void => {
      trackEvent('auth_attempt_started', {
        category: 'auth',
        action_type: 'auth_attempt_started',
        method,
        intent,
      } as Partial<AuthEventProperties>, { send_instantly: method !== 'email' })
    },

    stepViewed: (step: 'email' | 'signin' | 'signup' | 'verify' | 'reset'): boolean => {
      return trackEvent('auth_flow_step_viewed', {
        category: 'auth',
        action_type: 'auth_flow_step_viewed',
        step,
      } as Partial<AuthEventProperties>)
    },

    signupStarted: (method: 'email' | 'oauth_github' | 'oauth_google'): void => {
      trackEvent('signup_started', {
        category: 'auth',
        action_type: 'signup_started',
        method,
      } as Partial<AuthEventProperties>)
    },

    signupSucceeded: (method: 'email' | 'oauth_github' | 'oauth_google'): void => {
      trackEvent('signup_succeeded', {
        category: 'auth',
        action_type: 'signup_succeeded',
        method,
      } as Partial<AuthEventProperties>)
    },

    signupFailed: (method: 'email' | 'oauth_github' | 'oauth_google', errorMessage?: string): void => {
      trackEvent('signup_failed', {
        category: 'auth',
        action_type: 'signup_failed',
        method,
        error_message: errorMessage,
      } as Partial<AuthEventProperties>)
    },

    loginSucceeded: (method: 'email' | 'oauth_github' | 'oauth_google'): void => {
      trackEvent('login_succeeded', {
        category: 'auth',
        action_type: 'login_succeeded',
        method,
      } as Partial<AuthEventProperties>)
    },

    loginFailed: (method: 'email' | 'oauth_github' | 'oauth_google', errorMessage?: string): void => {
      trackEvent('login_failed', {
        category: 'auth',
        action_type: 'login_failed',
        method,
        error_message: errorMessage,
      } as Partial<AuthEventProperties>)
    },

    oauthLoginStarted: (method: OAuthAnalyticsMethod, oauthState?: string): void => {
      if (oauthState) {
        if (!canTrack()) {
          clearDeferredOAuthLoginStarted()
          return
        }
        // This call is followed synchronously by a full-page provider
        // navigation. If identity/bootstrap is not settled, store one bounded
        // intent instead of putting an event in the realm-local queue that the
        // navigation is about to destroy.
        if (!isPostHogReady || isAuthBootstrapPending) {
          deferOAuthLoginStarted(oauthState, method, analyticsConsent)
          return
        }
        clearDeferredOAuthLoginStarted()
      }
      trackEvent('auth_attempt_started', {
        category: 'auth',
        action_type: 'auth_attempt_started',
        method,
        intent: 'login_or_signup',
      } as Partial<AuthEventProperties>, { send_instantly: true })
      trackEvent('oauth_login_started', {
        category: 'auth',
        action_type: 'oauth_login_started',
        method,
      } as Partial<AuthEventProperties>, { send_instantly: true })
    },

    resumeOAuthLoginStarted: (method: OAuthAnalyticsMethod, oauthState: string): boolean => {
      if (!takeDeferredOAuthLoginStarted(oauthState, method, analyticsConsent))
        return false
      trackEvent('auth_attempt_started', {
        category: 'auth',
        action_type: 'auth_attempt_started',
        method,
        intent: 'login_or_signup',
      } as Partial<AuthEventProperties>, { send_instantly: true })
      trackEvent('oauth_login_started', {
        category: 'auth',
        action_type: 'oauth_login_started',
        method,
      } as Partial<AuthEventProperties>, { send_instantly: true })
      return true
    },

    logoutStarted: (): void => {
      trackEvent('logout_started', {
        category: 'auth',
        action_type: 'logout_started',
      } as Partial<AuthEventProperties>, { send_instantly: true })
    },

    logoutSucceeded: (): void => {
      trackEvent('logout_succeeded', {
        category: 'auth',
        action_type: 'logout_succeeded',
      } as Partial<AuthEventProperties>, { send_instantly: true })
    },

    logoutFailed: (errorMessage?: string): void => {
      trackEvent('logout_failed', {
        category: 'auth',
        action_type: 'logout_failed',
        error_message: errorMessage,
      } as Partial<AuthEventProperties>, { send_instantly: true })
    },

    sessionExpired: (): void => {
      trackEvent('session_expired', {
        category: 'auth',
        action_type: 'session_expired',
      } as Partial<AuthEventProperties>)
    },

    cliAuthViewed: (_clientName: string): void => {
      trackEvent('cli_auth_viewed', {
        category: 'auth',
        action_type: 'cli_auth_viewed',
      } as Partial<AuthEventProperties>)
    },

    cliAuthApproved: (_clientName: string): void => {
      trackEvent('cli_auth_approved', {
        category: 'auth',
        action_type: 'cli_auth_approved',
      } as Partial<AuthEventProperties>)
    },

    cliAuthFailed: (_clientName: string, errorMessage: string): void => {
      trackEvent('cli_auth_failed', {
        category: 'auth',
        action_type: 'cli_auth_failed',
        error_message: errorMessage,
      } as Partial<AuthEventProperties>)
    },

    cliAuthExpired: (_clientName: string): void => {
      trackEvent('cli_auth_expired', {
        category: 'auth',
        action_type: 'cli_auth_expired',
      } as Partial<AuthEventProperties>)
    },
  },

  /**
   * 支付追踪
   */
  payment: {
    pricingPageViewed: (status?: string): boolean => {
      return trackEvent('pricing_page_viewed', {
        category: 'payment',
        action_type: 'pricing_page_viewed',
        status,
      } as Partial<PaymentEventProperties>)
    },

    planSelected: (planTitle: string, billingCycle: string, properties?: { planId?: string, placement?: string, isAuthenticated?: boolean }): void => {
      trackEvent('plan_selected', {
        category: 'payment',
        action_type: 'plan_selected',
        plan_title: planTitle,
        billing_cycle: billingCycle,
        plan_id: properties?.planId,
        placement: properties?.placement,
        is_authenticated: properties?.isAuthenticated,
      } as Partial<PaymentEventProperties>)
    },

    billingCycleSelected: (billingCycle: 'monthly' | 'annual'): void => {
      trackEvent('pricing_billing_cycle_selected', {
        category: 'payment',
        action_type: 'pricing_billing_cycle_selected',
        billing_cycle: billingCycle,
      } as Partial<PaymentEventProperties>)
    },

    avatarUnitPackageSelected: (packageId: string, units: number, priceCents: number, source: string): void => {
      trackEvent('avatar_unit_package_selected', {
        category: 'payment',
        action_type: 'avatar_unit_package_selected',
        product_type: 'avatar_units',
        package_id: packageId,
        units,
        price_cents: priceCents,
        source,
      } as Partial<PaymentEventProperties>)
    },

    checkoutStarted: (context: CheckoutEventContext): void => {
      trackEvent('checkout_started', {
        category: 'payment',
        action_type: 'checkout_started',
        product_type: context.productType,
        plan_id: context.planId,
        package_id: context.packageId,
        billing_cycle: context.billingCycle,
        source: context.source,
      } as Partial<PaymentEventProperties>)
    },

    checkoutRedirected: (context: CheckoutEventContext): void => {
      trackEvent('checkout_redirected', {
        category: 'payment',
        action_type: 'checkout_redirected',
        product_type: context.productType,
        plan_id: context.planId,
        package_id: context.packageId,
        billing_cycle: context.billingCycle,
        source: context.source,
      } as Partial<PaymentEventProperties>, { send_instantly: true })
    },

    checkoutSucceeded: (context: CheckoutEventContext): void => {
      trackEvent('checkout_succeeded', {
        category: 'payment',
        action_type: 'checkout_succeeded',
        product_type: context.productType,
        plan_id: context.planId,
        package_id: context.packageId,
        billing_cycle: context.billingCycle,
        source: context.source,
      } as Partial<PaymentEventProperties>)
    },

    checkoutCancelled: (context: CheckoutEventContext): void => {
      trackEvent('checkout_cancelled', {
        category: 'payment',
        action_type: 'checkout_cancelled',
        product_type: context.productType,
        plan_id: context.planId,
        package_id: context.packageId,
        billing_cycle: context.billingCycle,
        source: context.source,
      } as Partial<PaymentEventProperties>)
    },

    checkoutFailed: (context: CheckoutEventContext): void => {
      trackEvent('checkout_failed', {
        category: 'payment',
        action_type: 'checkout_failed',
        product_type: context.productType,
        plan_id: context.planId,
        package_id: context.packageId,
        billing_cycle: context.billingCycle,
        source: context.source,
        reason: context.reason,
      } as Partial<PaymentEventProperties>)
    },
  },
}
